import AddIcon from '@mui/icons-material/Add';
import {Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, styled} from '@mui/material';
import {get, isNil, map, omit, some} from 'lodash';
import {FC, useMemo, useRef} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {AccordionHandle, FiltersAccordion} from '../../../../components/Filters';
import {CONTRACT_TABLE_FILTERS_CRITERIA} from '../../constants/contract-table-filters.constants';
import {setFilter, useContractsTableFiltersStore} from '../../stores/contracts-table-filters.store';
import {ContractFiltersFormValues, Criteria} from '../../types';
import {ContractsTableAccordionContainer} from './contracts-table-accordion-container';
import {ContractsTableCreateFilterFieldBuilder} from './contracts-table-create-filter-fileld-builder';

const DEFAULT_VALUES: ContractFiltersFormValues = {
  criteria: '',
  startDate: null,
  dueDate: null,
  status: '',
  companyId: '',
  companyName: '',
  contractNumber: '',
  counter: '',
  serviceType: '',
};

export const ContractsTableCreateFilterForm: FC = () => {
  const ref = useRef<AccordionHandle | null>(null);
  const filters = useContractsTableFiltersStore((state) => state.filters);
  const filterKeys = map(filters, 'key');
  const formApi = useForm<ContractFiltersFormValues>({defaultValues: DEFAULT_VALUES});
  const formValues = formApi.watch();
  const hasAnyValue = some(omit(formValues, 'criteria'), (v) => !isNil(v) && v !== '');

  const handleChangeCriteria = (event: SelectChangeEvent<Criteria>) => {
    formApi.reset({...DEFAULT_VALUES, criteria: event.target.value as Criteria});
  };

  const handleResetForm = () => {
    formApi.reset(DEFAULT_VALUES);
    ref.current?.close();
  };

  const handleFormSubmit = formApi.handleSubmit((values) => {
    const criteria = values.criteria;
    if (!criteria) return;
    setFilter({key: criteria, value: formValues[criteria]});
    handleResetForm();
  });

  const handleExpandFilter = () => {
    formApi.reset(DEFAULT_VALUES);
  };

  const criteriaOptions = useMemo(
    () => CONTRACT_TABLE_FILTERS_CRITERIA.filter(({value}) => !filterKeys.includes(value)),
    [filterKeys],
  );

  return (
    <ContractsTableAccordionContainer>
      <FiltersAccordion
        ref={ref}
        onChange={handleExpandFilter}
        summaryIcon={<StyledAddIcon />}
        summaryContent="Новый фильтр"
      >
        <Stack gap={2}>
          <FormProvider {...formApi}>
            <Controller
              control={formApi.control}
              name="criteria"
              render={({field}) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="criteria">Критерий фильтра</InputLabel>
                  <Select labelId="criteria" label="Критерий фильтра" {...field} onChange={handleChangeCriteria}>
                    {criteriaOptions.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <ContractsTableCreateFilterFieldBuilder />
          </FormProvider>
          <Stack justifyContent="flex-end" direction="row" spacing={2}>
            <StyledButton size="small" color="error" onClick={handleResetForm}>
              Отмена
            </StyledButton>
            <StyledButton size="small" disabled={!hasAnyValue} onClick={handleFormSubmit}>
              Применить
            </StyledButton>
          </Stack>
        </Stack>
      </FiltersAccordion>
    </ContractsTableAccordionContainer>
  );
};

const StyledButton = styled(Button)({
  fontWeight: 'bold',
});

const StyledAddIcon = styled(AddIcon)({
  marginRight: 8,
  fontSize: 22,
});
